import {
    addChildAtPath,
    branchOp,
    BRANCH_OPS,
    COMPARATORS,
    defaultGroup,
    defaultLeaf,
    isLeaf,
    removeAtPath,
    replaceAtPath,
    toggleOp
} from './condition-utils.js'
import styles from './condition-node.module.scss'

const MAX_DEPTH = 8

/**
 * Recursive renderer for a scenario condition tree. Read-only by default;
 * pass `editable` + `onChange` for the admin scenarios builder.
 *
 * @param {{
 *   node: any,
 *   onChange?: (next: any) => void,
 *   editable?: boolean,
 *   inputs?: string[],
 *   depth?: number,
 *   path?: Array<number|'not'>,
 *   rootRef?: any
 * }} props
 */
export function ConditionNode({
    node,
    onChange,
    editable = false,
    inputs,
    depth = 0,
    path,
    rootRef
}) {
    const isRoot = depth === 0 && !path
    const currentPath = path || []
    const tree = isRoot ? node : rootRef
    const emit = (next) => {
        if (onChange)
            onChange(next)
    }

    if (!node) {
        if (!editable)
            return <p className={styles.empty}>No conditions configured.</p>
        return (
            <div className={styles.empty}>
                <p>No conditions yet.</p>
                <div className={styles.toolbar}>
                    <button type="button" className={styles.actionBtn} onClick={() => emit(defaultGroup('and'))}>
                        + Add group
                    </button>
                    <button type="button" className={styles.actionBtn} onClick={() => emit(defaultLeaf())}>
                        + Add leaf
                    </button>
                </div>
            </div>
        )
    }

    const op = branchOp(node)

    if (op === 'and' || op === 'or') {
        const children = node[op]
        const canNest = depth < MAX_DEPTH - 1
        return (
            <div className={styles.branch} aria-level={depth + 1} role="treeitem">
                <div className={styles.branchHeader}>
                    {editable ? (
                        <OpToggle value={op} onChange={(next) => emit(replaceAtPath(tree, currentPath, toggleOp(node, [], next)))}/>
                    ) : (
                        <span className={styles.op}>{op}</span>
                    )}
                    {editable && !isRoot && (
                        <button
                            type="button"
                            className={styles.removeBtn}
                            aria-label="Remove group"
                            onClick={() => emit(removeAtPath(tree, currentPath))}
                        >×</button>
                    )}
                </div>
                <div className={styles.children}>
                    {children.map((child, i) => (
                        <ConditionNode
                            key={i}
                            node={child}
                            onChange={onChange}
                            editable={editable}
                            inputs={inputs}
                            depth={depth + 1}
                            path={[...currentPath, i]}
                            rootRef={tree}
                        />
                    ))}
                    {editable && (
                        <div className={styles.toolbar}>
                            <button
                                type="button"
                                className={styles.actionBtn}
                                onClick={() => emit(addChildAtPath(tree, currentPath, defaultLeaf()))}
                            >+ Leaf</button>
                            {canNest && (
                                <button
                                    type="button"
                                    className={styles.actionBtn}
                                    onClick={() => emit(addChildAtPath(tree, currentPath, defaultGroup('and')))}
                                >+ Group</button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        )
    }

    if (op === 'not') {
        return (
            <div className={styles.branch} aria-level={depth + 1} role="treeitem">
                <div className={styles.branchHeader}>
                    {editable ? (
                        <OpToggle value="not" onChange={(next) => emit(replaceAtPath(tree, currentPath, toggleOp(node, [], next)))}/>
                    ) : (
                        <span className={styles.op}>not</span>
                    )}
                    {editable && !isRoot && (
                        <button
                            type="button"
                            className={styles.removeBtn}
                            aria-label="Remove group"
                            onClick={() => emit(removeAtPath(tree, currentPath))}
                        >×</button>
                    )}
                </div>
                <div className={styles.children}>
                    <ConditionNode
                        node={node.not}
                        onChange={onChange}
                        editable={editable}
                        inputs={inputs}
                        depth={depth + 1}
                        path={[...currentPath, 'not']}
                        rootRef={tree}
                    />
                </div>
            </div>
        )
    }

    // Leaf
    if (!editable) {
        return (
            <div className={styles.leaf} aria-level={depth + 1} role="treeitem">
                <span className={styles.input}>{node.input || node.metric || 'value'}</span>
                <span className={styles.cmp}>{node.cmp || node.op || '='}</span>
                <span className={styles.value}>{String(node.value ?? node.threshold ?? '')}</span>
            </div>
        )
    }

    const onLeafChange = (patch) => {
        const nextLeaf = {...node, ...patch}
        emit(replaceAtPath(tree, currentPath, nextLeaf))
    }

    return (
        <div className={styles.leafEdit} aria-level={depth + 1} role="treeitem">
            {inputs && inputs.length > 0 ? (
                <select
                    className={styles.leafInput}
                    value={node.input || ''}
                    onChange={(e) => onLeafChange({input: e.target.value})}
                    aria-label="Input"
                >
                    <option value="">— select input —</option>
                    {inputs.map(name => <option key={name} value={name}>{name}</option>)}
                </select>
            ) : (
                <input
                    type="text"
                    className={styles.leafInput}
                    value={node.input || ''}
                    onChange={(e) => onLeafChange({input: e.target.value})}
                    placeholder="metric.path"
                    aria-label="Input"
                />
            )}
            <select
                className={styles.leafCmp}
                value={node.cmp || '>'}
                onChange={(e) => onLeafChange({cmp: e.target.value})}
                aria-label="Comparator"
            >
                {COMPARATORS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input
                type="number"
                className={styles.leafValue}
                value={node.value ?? 0}
                onChange={(e) => onLeafChange({value: e.target.value === '' ? '' : Number(e.target.value)})}
                aria-label="Value"
            />
            <button
                type="button"
                className={styles.removeBtn}
                aria-label="Remove leaf"
                onClick={() => emit(removeAtPath(tree, currentPath))}
            >×</button>
        </div>
    )
}

/**
 * @param {{value: 'and'|'or'|'not', onChange: (next: 'and'|'or'|'not') => void}} props
 */
function OpToggle({value, onChange}) {
    return (
        <div className={styles.opToggle} role="radiogroup" aria-label="Operator">
            {BRANCH_OPS.map(op => (
                <button
                    key={op}
                    type="button"
                    role="radio"
                    aria-checked={op === value}
                    className={op === value ? styles.opCellActive : styles.opCell}
                    onClick={() => onChange(op)}
                >{op}</button>
            ))}
        </div>
    )
}
